import { useQuery } from '@tanstack/react-query'
import { makeRiverRpcClient, StreamsView, UnauthenticatedClient } from '@towns-protocol/sdk'
import { useEnvironment } from 'hooks/useEnvironment'
import { AuthStatus, makeProviderFromConfig, MINUTE_MS, useConnectivity } from 'use-towns-client'

async function fetchSpaceMembers(
    networkId: string,
    riverChain: ReturnType<typeof useEnvironment>['riverChain'],
    riverChainConfig: ReturnType<typeof useEnvironment>['riverChainConfig'],
): Promise<string[]> {
    if (!riverChainConfig) {
        throw new Error('River chain config not available')
    }

    const provider = makeProviderFromConfig(riverChain)
    const rpcClient = await makeRiverRpcClient(provider, riverChainConfig)
    const client = new UnauthenticatedClient(rpcClient)
    const view = new StreamsView('', undefined)
    const stream = await client.getStream(networkId, view)

    return Array.from(stream.getMembers().joinedUsers)
}

/**
 * Hook to fetch members from a space using an unauthenticated client.
 * This is useful for displaying member information when the user is not authenticated.
 *
 * @param networkId - The network ID of the space (from spaceInfo.networkId)
 * @returns Object containing member IDs array and loading state
 */
export const useUnauthenticatedMembers = (networkId: string | undefined) => {
    const { authStatus } = useConnectivity()
    const { riverChainConfig, riverChain } = useEnvironment()

    const { data: memberIds = [], isLoading } = useQuery({
        queryKey: ['unauthenticated-members', networkId],
        queryFn: () => fetchSpaceMembers(networkId!, riverChain, riverChainConfig),
        enabled:
            !!networkId && !!riverChainConfig && authStatus !== AuthStatus.EvaluatingCredentials,
        staleTime: MINUTE_MS * 5,
        gcTime: MINUTE_MS * 10,
    })

    return {
        memberIds,
        isLoading,
    }
}
